import { Assessment, LiveSession } from "./store/slices/assessments";

export function isLiveSessionRegistered<T extends Pick<LiveSession, "users">>({ users }: T, currUserId?: string) {
	return users.some(({ userId }) => userId === currUserId);
}

export function filterRegisteredLiveSessions<T extends Pick<LiveSession, "users">>(sessions: T[], currUserId?: string) {
	return sessions.filter((session) => isLiveSessionRegistered(session, currUserId));
}

export function isLiveSessionStartable(
	{ startTime, started }: Pick<LiveSession, "startTime" | "started">,
	currTimestamp = Date.now(),
) {
	const startTimestamp = new Date(startTime).getTime();
	return currTimestamp > startTimestamp || started;
}

export function isLiveSessionEnded({ endTime }: Pick<LiveSession, "endTime">, currTimestamp = Date.now()) {
	return currTimestamp >= new Date(endTime).getTime();
}

export function isLiveSessionActive(
	session: Pick<LiveSession, "startTime" | "endTime" | "started">,
	currTimestamp = Date.now(),
) {
	return isLiveSessionStartable(session, currTimestamp) && !isLiveSessionEnded(session, currTimestamp);
}

export function isLiveSessionRegistrationStarted(
	{ registrationStartTime }: Pick<LiveSession, "registrationStartTime">,
	currTimestamp = Date.now(),
) {
	return !registrationStartTime || currTimestamp >= new Date(registrationStartTime).getTime();
}

export function isLiveSessionRegistrationEnded(
	{ endTime, registrationEndTime }: Pick<LiveSession, "endTime" | "registrationEndTime">,
	currTimestamp = Date.now(),
) {
	return currTimestamp >= new Date(registrationEndTime ? registrationEndTime : endTime).getTime();
}

export function isLiveSessionRegistrationActive(
	session: Pick<LiveSession, "endTime" | "registrationStartTime" | "registrationEndTime">,
	currTimestamp = Date.now(),
) {
	return (
		isLiveSessionRegistrationStarted(session, currTimestamp) && !isLiveSessionRegistrationEnded(session, currTimestamp)
	);
}

export function isAssessmentTaken({ attemptsCount }: Assessment) {
	return attemptsCount > 0;
}

export function isAssessmentActive(assessment: Assessment, currUserId?: string, currTimestamp = Date.now()) {
	const { proctoringOptions, liveSessions } = assessment;
	return (
		!isAssessmentTaken(assessment) &&
		(!proctoringOptions?.isLiveProctored ||
			filterRegisteredLiveSessions(liveSessions, currUserId).some((session) =>
				isLiveSessionActive(session, currTimestamp),
			))
	);
}

export function isAssessmentUpcoming(assessment: Assessment, currUserId?: string, currTimestamp = Date.now()) {
	const { proctoringOptions, liveSessions } = assessment;
	return (
		!isAssessmentTaken(assessment) &&
		proctoringOptions?.isLiveProctored &&
		filterRegisteredLiveSessions(liveSessions, currUserId).some(
			(session) => !isLiveSessionStartable(session, currTimestamp) && !isLiveSessionEnded(session, currTimestamp),
		)
	);
}

export function isAssessmentScheduleRequired(assessment: Assessment, currUserId?: string, currTimestamp = Date.now()) {
	const { proctoringOptions, liveSessions } = assessment;
	return (
		!isAssessmentTaken(assessment) &&
		proctoringOptions?.isLiveProctored &&
		filterRegisteredLiveSessions(liveSessions, currUserId).length === 0 &&
		liveSessions.filter((session) => !isLiveSessionRegistrationEnded(session, currTimestamp)).length > 0
	);
}

export function assessmentSessionsAvailable(
	{ proctoringOptions, liveSessions }: Assessment,
	currTimestamp = Date.now(),
) {
	return (
		proctoringOptions?.isLiveProctored &&
		liveSessions.some((session) => !isLiveSessionRegistrationEnded(session, currTimestamp))
	);
}

export function isAssessmentCompleted(assessment: Assessment, currUserId?: string, currTimestamp = Date.now()) {
	const { proctoringOptions } = assessment;
	const registeredLiveSessions = filterRegisteredLiveSessions(assessment.liveSessions, currUserId);
	return (
		isAssessmentTaken(assessment) ||
		(proctoringOptions?.isLiveProctored &&
			((registeredLiveSessions.length > 0 &&
				registeredLiveSessions.every((session) => isLiveSessionEnded(session, currTimestamp))) ||
				(registeredLiveSessions.length === 0 && !assessmentSessionsAvailable(assessment, currTimestamp))))
	);
}
